import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { UserContext, CompanyContext } from '../App';
import Navigation from './Navigation';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  Receipt,
  ShoppingCart,
  ShieldCheck,
  Send,
  CheckCircle2,
  Lock,
  RefreshCw,
  Calendar,
  User as UserIcon,
} from 'lucide-react';
import InvoiceGeneratorFixed from './InvoiceGeneratorFixed';

const InvoiceManagement = () => {
  const { user, API } = useContext(UserContext);
  const { company } = useContext(CompanyContext);

  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceGen, setShowInvoiceGen] = useState(false);
  const [selectedSaleData, setSelectedSaleData] = useState(null);
  const [transmitting, setTransmitting] = useState(false);

  const productMap = useMemo(() => {
    const m = {};
    products.forEach((p) => (m[p.id] = p));
    return m;
  }, [products]);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => (m[u.id] = u));
    return m;
  }, [users]);

  // Set of sale IDs already linked to an invoice
  const billedSaleIds = useMemo(() => {
    const s = new Set();
    invoices.forEach((inv) => (inv.linked_sale_ids || []).forEach((id) => s.add(id)));
    return s;
  }, [invoices]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [salesRes, productsRes, invoicesRes, usersRes] = await Promise.all([
        axios.get(`${API}/sales`),
        axios.get(`${API}/products`),
        axios.get(`${API}/invoices`),
        axios.get(`${API}/users`, { params: { user_role: user?.role } }).catch(() => ({ data: [] })),
      ]);
      const salesArr = Array.isArray(salesRes.data) ? salesRes.data : [];
      // Sort newest first
      salesArr.sort((a, b) => {
        const da = `${a.date || ''} ${a.time || ''}`;
        const db = `${b.date || ''} ${b.time || ''}`;
        return db.localeCompare(da);
      });
      setSales(salesArr);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleGenerateInvoice = (sale) => {
    const product = productMap[sale.product_id];
    if (!product) {
      toast.error('Produit introuvable pour cette vente');
      return;
    }
    setSelectedSaleData({
      products: [
        {
          id: product.id,
          name: product.name,
          price: product.price,
          quantity: sale.quantity_sold,
        },
      ],
      linked_sale_ids: [sale.id],
    });
    setShowInvoiceGen(true);
  };

  const handleInvoiceModalClose = async () => {
    setShowInvoiceGen(false);
    setSelectedSaleData(null);
    // Refresh list to capture newly generated invoice
    await loadAll();
  };

  const toggleCertify = async (invoice, checked) => {
    if (invoice.transmitted_to_fne) {
      toast.error('Facture déjà transmise — verrouillée');
      return;
    }
    try {
      await axios.patch(`${API}/invoices/${invoice.id}/certify`, {
        certified: checked,
        user_id: user.id,
      });
      toast.success(checked ? 'Facture certifiée' : 'Certification retirée');
      await loadAll();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Erreur de certification');
    }
  };

  const handleTransmitFNE = async () => {
    const toSend = invoices.filter(
      (inv) => inv.certified && !inv.transmitted_to_fne
    );
    if (toSend.length === 0) {
      toast.info('Aucune facture certifiée non transmise à envoyer');
      return;
    }
    if (!window.confirm(
      `Transmettre ${toSend.length} facture(s) certifiée(s) à la FNE de la DGI ?\n\n(Mode test — transmission MOCKÉE)`
    )) return;
    setTransmitting(true);
    try {
      const res = await axios.post(`${API}/invoices/transmit-fne`, {
        invoice_ids: toSend.map((i) => i.id),
      });
      toast.success(
        `${res.data.transmitted}/${res.data.total} facture(s) transmise(s) avec succès`
      );
      await loadAll();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Erreur de transmission FNE');
    } finally {
      setTransmitting(false);
    }
  };

  const formatXOF = (n) =>
    `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} F CFA`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="invoice-management-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Receipt className="h-8 w-8 text-orange-600" />
              Gestion des Factures
            </h1>
            <p className="text-gray-600 mt-1">
              Générez des reçus à partir des ventes et certifiez-les avant transmission à la FNE de la DGI
            </p>
          </div>
          <Button
            onClick={loadAll}
            variant="outline"
            disabled={loading}
            data-testid="invoice-mgmt-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Section 1 — Ventes à facturer */}
        <Card className="mb-6 border-2 border-blue-200" data-testid="sales-to-invoice-section">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-blue-600" />
                Ventes à facturer
              </span>
              <Badge variant="secondary">{sales.length} vente(s)</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Chargement…</div>
            ) : sales.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Aucune vente enregistrée.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="text-left px-4 py-2">Date / Heure</th>
                      <th className="text-left px-4 py-2">Vendeur</th>
                      <th className="text-left px-4 py-2">Produit</th>
                      <th className="text-right px-4 py-2">Quantité</th>
                      <th className="text-right px-4 py-2">Prix unitaire</th>
                      <th className="text-right px-4 py-2">Total</th>
                      <th className="text-center px-4 py-2">Statut</th>
                      <th className="text-center px-4 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((sale) => {
                      const product = productMap[sale.product_id];
                      const seller = userMap[sale.user_id];
                      const total = (product?.price || 0) * (sale.quantity_sold || 0);
                      const billed = billedSaleIds.has(sale.id);
                      return (
                        <tr
                          key={sale.id}
                          className="border-b border-gray-100 hover:bg-orange-50/50"
                          data-testid={`sale-row-${sale.id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 text-gray-700">
                              <Calendar className="h-3 w-3" />
                              {sale.date} <span className="text-gray-400 ml-1">{sale.time}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-1">
                              <UserIcon className="h-3 w-3 text-gray-400" />
                              {seller?.username || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium">
                            {product?.name || <span className="text-red-500">Produit supprimé</span>}
                          </td>
                          <td className="px-4 py-3 text-right">{sale.quantity_sold}</td>
                          <td className="px-4 py-3 text-right">{formatXOF(product?.price)}</td>
                          <td className="px-4 py-3 text-right font-bold">{formatXOF(total)}</td>
                          <td className="px-4 py-3 text-center">
                            {billed ? (
                              <Badge className="bg-green-100 text-green-700 border-green-300">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Déjà facturé
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500">À facturer</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              onClick={() => handleGenerateInvoice(sale)}
                              disabled={!product}
                              variant={billed ? 'outline' : 'default'}
                              className={billed ? '' : 'bg-orange-600 hover:bg-orange-700'}
                              data-testid={`generate-invoice-btn-${sale.id}`}
                            >
                              <Receipt className="h-4 w-4 mr-1" />
                              {billed ? 'Refacturer' : 'Générer reçu'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 2 — Certification des factures */}
        <Card className="border-2 border-emerald-200" data-testid="certification-section">
          <CardHeader className="bg-emerald-50">
            <CardTitle className="flex items-center justify-between flex-wrap gap-3">
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
                Certification des factures
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary">{invoices.length} facture(s)</Badge>
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                  {invoices.filter((i) => i.certified).length} certifiée(s)
                </Badge>
                <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                  {invoices.filter((i) => i.transmitted_to_fne).length} transmise(s)
                </Badge>
                <Button
                  onClick={handleTransmitFNE}
                  disabled={transmitting || invoices.filter((i) => i.certified && !i.transmitted_to_fne).length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="transmit-fne-btn"
                >
                  <Send className={`h-4 w-4 mr-2 ${transmitting ? 'animate-pulse' : ''}`} />
                  Transmettre à la FNE (DGI)
                </Button>
              </div>
            </CardTitle>
            <p className="text-xs text-emerald-700 mt-1">
              Seules les factures cochées <strong>certifiées</strong> seront transmises à l'API FNE de la DGI Côte d'Ivoire.
              Une fois transmise, une facture est <strong>verrouillée</strong>.
              <span className="ml-2 italic">(Transmission actuellement en mode MOCK pour tests.)</span>
            </p>
          </CardHeader>
          <CardContent className="p-0">
            {invoices.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Aucune facture générée pour le moment.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="text-center px-4 py-2 w-16">Certifier</th>
                      <th className="text-left px-4 py-2">N° Facture</th>
                      <th className="text-left px-4 py-2">Date</th>
                      <th className="text-left px-4 py-2">Client</th>
                      <th className="text-right px-4 py-2">Montant TTC</th>
                      <th className="text-center px-4 py-2">Statut</th>
                      <th className="text-left px-4 py-2">Référence FNE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => {
                      const customer = inv.customer_info?.is_company
                        ? inv.customer_info.company_name
                        : inv.customer_info?.customer_name || 'Client comptoir';
                      const isLocked = !!inv.transmitted_to_fne;
                      return (
                        <tr
                          key={inv.id}
                          className="border-b border-gray-100 hover:bg-emerald-50/40"
                          data-testid={`invoice-row-${inv.id}`}
                        >
                          <td className="px-4 py-3 text-center">
                            <Checkbox
                              checked={!!inv.certified}
                              onCheckedChange={(v) => toggleCertify(inv, !!v)}
                              disabled={isLocked}
                              data-testid={`certify-checkbox-${inv.id}`}
                            />
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">
                            {inv.invoice_number}
                          </td>
                          <td className="px-4 py-3">
                            {inv.sale_date} <span className="text-gray-400">{inv.sale_time}</span>
                          </td>
                          <td className="px-4 py-3">{customer}</td>
                          <td className="px-4 py-3 text-right font-bold">
                            {formatXOF(inv.total_to_pay)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isLocked ? (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                                <Lock className="h-3 w-3 mr-1" />
                                Transmise FNE
                              </Badge>
                            ) : inv.certified ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                                <ShieldCheck className="h-3 w-3 mr-1" />
                                Certifiée
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500">Brouillon</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-600">
                            {inv.fne_reference || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal de génération */}
        {showInvoiceGen && (
          <InvoiceGeneratorFixed
            isOpen={showInvoiceGen}
            onClose={handleInvoiceModalClose}
            saleData={selectedSaleData}
          />
        )}
      </div>
    </div>
  );
};

export default InvoiceManagement;
